import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon';

export interface StatCardData {
  value: string | number;
  label: string;
  subValue?: string;
  leftValue?: string;
  leftLabel?: string;
  rightValue?: string;
  rightLabel?: string;
  icon: string;
  color: string;
  variant?: 'default' | 'compact' | 'split';
  iconBgColor?: string;
  valueSize?: 'sm' | 'md' | 'lg';
}

@Component({
  selector: 'app-stat-card',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    <div class="bg-white rounded-lg border border-gray-200 p-6 h-full flex flex-col justify-center">
      
      <div *ngIf="variant === 'split'" class="w-full">
        <div class="flex items-center gap-3 mb-6">
          <div
            [ngClass]="color"
            class="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0"
          >
            <app-icon [name]="icon" className="w-6 h-6" *ngIf="showIcon"></app-icon>
          </div>
          <p class="text-sm text-gray-700 font-medium break-words">{{ label }}</p>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="min-w-0"> <p 
              class="font-bold break-words leading-tight"
              [ngClass]="getDynamicClasses(leftValue)"
              [title]="leftValue"
            >{{ leftValue }}</p>
            <p class="text-sm text-gray-500 truncate" [title]="leftLabel">{{ leftLabel }}</p>
          </div>
          <div class="min-w-0">
            <p 
              class="font-bold break-words leading-tight"
              [ngClass]="getDynamicClasses(rightValue)"
              [title]="rightValue"
            >{{ rightValue }}</p>
            <p class="text-sm text-gray-500 truncate" [title]="rightLabel">{{ rightLabel }}</p>
          </div>
        </div>
      </div>

      <div *ngIf="!variant || variant === 'default'" class="w-full">
        <div class="flex items-center justify-between mb-4">
          <div
            [ngClass]="color"
            class="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0"
          >
            <app-icon [name]="icon" className="w-6 h-6" *ngIf="showIcon"></app-icon>
          </div>
        </div>

        <p
          class="font-bold break-words leading-tight"
          [ngClass]="valueClasses"
          [class.mb-1]="!subValue"
          [title]="value"
        >
          {{ value }}
        </p>

        <p *ngIf="subValue" class="text-xs text-gray-500 mb-1 break-words">
          {{ subValue }}
        </p>

        <p class="text-sm text-gray-500 break-words">{{ label }}</p>
      </div>

      <div *ngIf="variant === 'compact'" class="w-full">
        <div class="flex items-center gap-3 mb-3">
          <div
            [ngClass]="iconBgColor || 'bg-gray-50'"
            class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            *ngIf="showIcon"
          >
            <app-icon [name]="icon" [className]="'w-5 h-5 ' + color"></app-icon>
          </div>
          <p class="text-sm text-gray-500 break-words line-clamp-2" [title]="label">{{ label }}</p>
        </div>

        <p 
          class="font-bold break-words leading-tight"
          [ngClass]="valueClassesCompact"
          [title]="value"
        >
          {{ value }}
        </p>

        <p *ngIf="subValue" class="text-xs text-gray-500 mt-1 break-words">
          {{ subValue }}
        </p>
      </div>
    </div>
  `,
})
export class StatCardComponent {
  @Input() value: string | number = '';
  @Input() label: string = '';
  @Input() subValue?: string;
  @Input() leftValue?: string;
  @Input() leftLabel?: string;
  @Input() rightValue?: string;
  @Input() rightLabel?: string;
  @Input() icon: string = '';
  @Input() color: string = '';
  @Input() variant: 'default' | 'compact' | 'split' = 'default';
  @Input() iconBgColor?: string;
  @Input() valueSize?: 'sm' | 'md' | 'lg' = 'md'; // Tamanho PREFERIDO
  @Input() showIcon: boolean = true;
  @Input() valueColor?: string;

  /**
   * Calcula as classes dinamicamente baseada no tamanho do conteúdo
   */
  get valueClasses(): { [key: string]: boolean } {
    return this.calculateSizeClasses(this.value);
  }

  get valueClassesCompact(): { [key: string]: boolean } {
    return this.calculateSizeClasses(this.value);
  }

  /**
   * Helper para o layout 'split' ou uso direto no template
   */
  getDynamicClasses(val: string | number | undefined): { [key: string]: boolean } {
    return this.calculateSizeClasses(val || '');
  }

  /**
   * Lógica central de redimensionamento
   * Se o texto for longo, reduz o tamanho da fonte independente da preferência valueSize
   */
  private calculateSizeClasses(val: string | number): { [key: string]: boolean } {
    const classes: { [key: string]: boolean } = {};
    const stringVal = String(val);
    const length = stringVal.length;

    // Definição de cor
    const colorClass = this.valueColor || 'text-gray-900';
    classes[colorClass] = true;

    // Lógica de redução automática de fonte (AUTO-SCALE)
    // Se tiver mais de 13 caracteres (ex: 123.456.789,00), força texto menor
    if (length > 13) {
      classes['text-lg'] = true; // Força pequeno para caber
      return classes;
    }

    // Se tiver mais de 9 caracteres, usa no máximo XL
    if (length > 9) {
      classes['text-xl'] = true;
      return classes;
    }

    // Se for curto, respeita a configuração do input valueSize
    if (this.valueSize === 'lg' || !this.valueSize) {
      classes['text-3xl'] = true;
    } else if (this.valueSize === 'md') {
      classes['text-2xl'] = true;
    } else if (this.valueSize === 'sm') {
      classes['text-xl'] = true;
    }

    return classes;
  }
}